/* Custom element: <fade-slider>
   Cycles its [data-slide] children through a CSS opacity crossfade.
   Generic — any section can reuse it for a one-at-a-time fade effect.
   No external slider library — plain interval + class toggling.

   Add `data-mobile-only` to restrict the effect to the theme's mobile
   breakpoint: on desktop the slider pauses and every slide becomes
   focusable, so the section's own CSS can show them all statically. */

(() => {
  'use strict';

  const SELECTORS = {
    slide: '[data-slide]',
    focusable: 'a, button',
  };

  const CLASSES = {
    active: 'is-active',
  };

  const DEFAULT_INTERVAL = 4000;
  const MOBILE_QUERY = '(max-width: 749px)';

  class FadeSlider extends HTMLElement {
    #listeners = null;
    #timer = null;
    #slides = [];
    #activeIndex = 0;
    #interval = DEFAULT_INTERVAL;
    #mobileOnly = false;
    #reducedMotion = false;

    connectedCallback() {
      this.#slides = Array.from(this.querySelectorAll(SELECTORS.slide));
      if (this.#slides.length < 2) return;

      this.#interval = parseInt(this.getAttribute('data-interval'), 10) || DEFAULT_INTERVAL;
      this.#mobileOnly = this.hasAttribute('data-mobile-only');
      this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#activeIndex = this.#slides.findIndex((slide) => slide.classList.contains(CLASSES.active));
      if (this.#activeIndex === -1) {
        this.#activeIndex = 0;
        this.#slides[0].classList.add(CLASSES.active);
      }

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;

      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('focusout', () => this.play(), { signal });

      if (this.#mobileOnly) {
        document.addEventListener('theme:resize:width', () => this.#syncWithViewport(), { signal });
      }

      this.#syncWithViewport();
    }

    disconnectedCallback() {
      this.pause();
      this.#listeners?.abort();
      this.#listeners = null;
    }

    /** Start cycling. Public so a parent section can resume the slider. */
    play() {
      if (this.#timer) return;
      this.#timer = window.setInterval(() => this.#next(), this.#interval);
    }

    /** Stop cycling. Public so a parent section can hold the slider still. */
    pause() {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }

    /**
     * @returns {boolean} true when `data-mobile-only` is set and the viewport
     *   is above the theme's mobile breakpoint.
     */
    #isDesktopViewport() {
      if (!this.#mobileOnly) return false;

      return typeof window.theme?.isMobile === 'boolean'
        ? !window.theme.isMobile
        : !window.matchMedia(MOBILE_QUERY).matches;
    }

    /** Pause and expose every slide on desktop; cycle on mobile. */
    #syncWithViewport() {
      if (this.#isDesktopViewport()) {
        this.pause();
        for (const slide of this.#slides) {
          for (const element of slide.querySelectorAll(SELECTORS.focusable)) {
            element.removeAttribute('tabindex');
          }
        }
        return;
      }

      this.#updateFocusability();
      if (!this.#reducedMotion) this.play();
    }

    /** Advance to the next slide, wrapping at the end. */
    #next() {
      const current = this.#slides[this.#activeIndex];
      this.#activeIndex = (this.#activeIndex + 1) % this.#slides.length;
      const upcoming = this.#slides[this.#activeIndex];

      current.classList.remove(CLASSES.active);
      upcoming.classList.add(CLASSES.active);
      this.#updateFocusability();
    }

    /** Keep offscreen slides out of the tab order. */
    #updateFocusability() {
      for (const slide of this.#slides) {
        const isActive = slide.classList.contains(CLASSES.active);
        for (const element of slide.querySelectorAll(SELECTORS.focusable)) {
          element.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      }
    }
  }

  if (!customElements.get('fade-slider')) {
    customElements.define('fade-slider', FadeSlider);
  }
})();
